import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { architectureWorkspaceKeys } from "@/application/query-keys/architecture-workspace.keys";
import { architectureWorkspaceRepository } from "@/infrastructure/repositories/architecture-workspace.supabase.repository";
import type { ArchitectureWorkspaceStateRow } from "@/infrastructure/supabase/database.types";

export type ArchitectureWorkspacePatch = Partial<
  Pick<
    ArchitectureWorkspaceStateRow,
    | "compact_positions"
    | "compact_stage_highlights"
    | "map_positions"
    | "map_stage_highlights"
    | "map_nodes"
    | "map_edges"
    | "b2b_users_nodes"
    | "b2b_users_edges"
    | "b2b_users_positions"
    | "b2b_users_highlights"
  >
>;

export function useArchitectureWorkspace() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: architectureWorkspaceKeys.singleton(),
    queryFn: () => architectureWorkspaceRepository.get(),
    staleTime: 15_000,
  });

  useEffect(() => {
    return architectureWorkspaceRepository.subscribeToChanges(() => {
      void queryClient.invalidateQueries({ queryKey: architectureWorkspaceKeys.singleton() });
    });
  }, [queryClient]);

  const patchMutation = useMutation({
    mutationFn: (patch: ArchitectureWorkspacePatch) => architectureWorkspaceRepository.patch(patch),
    onSuccess: (row) => {
      queryClient.setQueryData(architectureWorkspaceKeys.singleton(), row);
    },
  });

  return {
    workspace: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    patch: patchMutation.mutate,
    patchAsync: patchMutation.mutateAsync,
    isPatching: patchMutation.isPending,
  };
}
